import { GridRowId } from "@mui/x-data-grid";

import { IHotelClinicPassengerType } from "@/models/hotel-clinic.model";
import { ClinicUnassignedEditableFields } from "@/stores/use-clinic-arrival-unassigned-passengers-table";

import { HotelClinicListItem } from "../types";

/** Matches transport-svc `Req_HotelClinicCreateTaskPassenger` (no extra fields). */
export type HotelClinicCreateTaskPassengerPayload =
    | { type: IHotelClinicPassengerType.CLINIC; pickUp: string; operationId: string }
    | { type: IHotelClinicPassengerType.CUSTOM; passengerDbId: string };

export type CreateHotelClinicTaskRequestPayload = {
    driverUserId: string;
    passengers: HotelClinicCreateTaskPassengerPayload[];
};

/** Bulk passenger: create-task passenger + its own driverUserId (flat list, mixed drivers). */
export type HotelClinicBulkPassengerPayload =
    | { type: IHotelClinicPassengerType.CLINIC; driverUserId: string; operationId: string; pickUp: string }
    | { type: IHotelClinicPassengerType.CUSTOM; driverUserId: string; passengerDbId: string };

/** Body for `POST /hotel-clinic/tasks/bulk`. */
export type Req_CreateHotelClinicTasksInBulk = {
    passengers: HotelClinicBulkPassengerPayload[];
    /**
     * clinic→hotel (departure) pool: force one independent task per passenger — skips the
     * merge-into-existing + 15-min auto-grouping. Omit for hotel→clinic (arrival) grouped behavior.
     */
    uniqueTask?: boolean;
};

/** Key the bulk response `id` maps back to (operationId for clinic, passengerDbId for custom). */
export function bulkPassengerKey(p: HotelClinicBulkPassengerPayload): string {
    return p.type === IHotelClinicPassengerType.CLINIC ? p.operationId : p.passengerDbId;
}

/** Build create-task payload from `changes` entry (no full row; `ctx` set in `processRowUpdate`). */
export function buildHotelClinicCreateTaskPassengerFromChange(
    change: ClinicUnassignedEditableFields
): HotelClinicCreateTaskPassengerPayload | null {
    if (change._isPlaceholder || !change.ctx) return null;
    const c = change.ctx;
    if (c.type === IHotelClinicPassengerType.CLINIC) {
        const pickUp = c.pickUp?.trim();
        const operationId = c.operationId?.trim();
        if (!pickUp || !operationId) return null;
        return { type: IHotelClinicPassengerType.CLINIC, pickUp, operationId };
    }
    const passengerDbId = c.passengerDbId?.trim();
    if (!passengerDbId) return null;
    return { type: IHotelClinicPassengerType.CUSTOM, passengerDbId };
}

/** Turn one `changes` entry into a driver-tagged bulk passenger; reuses the base builder. */
export function buildHotelClinicBulkPassengerFromChange(
    change: ClinicUnassignedEditableFields
): HotelClinicBulkPassengerPayload | null {
    const driverUserId = change.userId != null ? String(change.userId).trim() : "";
    if (!driverUserId) return null;
    const base = buildHotelClinicCreateTaskPassengerFromChange(change);
    if (!base) return null;
    return { ...base, driverUserId };
}

/**
 * Build the flat bulk passenger list across ALL drivers, plus a `key → rowIds` map used to map
 * each per-passenger response entry back to its grid row(s). Key is operationId | passengerDbId;
 * values are arrays so duplicate ids (and the response item) clear every matching row.
 */
export function buildHotelClinicBulkRequest(
    rowIds: GridRowId[],
    changes: Record<GridRowId, ClinicUnassignedEditableFields>
): { passengers: HotelClinicBulkPassengerPayload[]; keyToRowIds: Map<string, GridRowId[]> } {
    const passengers: HotelClinicBulkPassengerPayload[] = [];
    const keyToRowIds = new Map<string, GridRowId[]>();

    for (const rowId of rowIds) {
        const ch = changes[rowId];
        if (!ch || ch._isPlaceholder) continue;
        const passenger = buildHotelClinicBulkPassengerFromChange(ch);
        if (!passenger) continue;
        passengers.push(passenger);
        const key = bulkPassengerKey(passenger);
        keyToRowIds.set(key, [...(keyToRowIds.get(key) ?? []), rowId]);
    }

    return { passengers, keyToRowIds };
}

export function buildHotelClinicCreateTaskPassenger(
    row: HotelClinicListItem
): HotelClinicCreateTaskPassengerPayload | null {
    if (row.type === IHotelClinicPassengerType.CLINIC) {
        const pickUp = row.pickUp?.trim();
        const operationId = row.id?.trim();
        if (!pickUp || !operationId) return null;
        return {
            type: IHotelClinicPassengerType.CLINIC,
            pickUp,
            operationId,
        };
    }
    if (row.type === IHotelClinicPassengerType.CUSTOM) {
        const passengerDbId = row.id?.trim();
        if (!passengerDbId) return null;
        return {
            type: IHotelClinicPassengerType.CUSTOM,
            passengerDbId,
        };
    }
    return null;
}

export function isRowReadyForDriverAssign(change: ClinicUnassignedEditableFields | undefined): boolean {
    if (!change) return false;
    const u = change.userId;
    if (u == null || String(u).trim() === "") return false;
    if (change._isPlaceholder) return false;
    return buildHotelClinicCreateTaskPassengerFromChange(change) !== null;
}
